/**
 * Mobile Todos View
 * Shows operational todos with filtering and completion
 */

import React, { useState, useEffect } from 'react';
import {
  CheckSquare,
  Check,
  Clock,
  AlertTriangle,
  Calendar,
  Gauge,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { mobileAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';

function getPriorityColor(priority) {
  switch (priority) {
    case 'high':
      return 'bg-red-500';
    case 'medium':
      return 'bg-yellow-500';
    default:
      return 'bg-slate-500';
  }
}

function getStatusIcon(status) {
  switch (status) {
    case 'done':
      return <Check className="w-5 h-5 text-green-400" />;
    case 'overdue':
      return <AlertTriangle className="w-5 h-5 text-red-400" />;
    default:
      return <Clock className="w-5 h-5 text-blue-400" />;
  }
}

export default function TodosView() {
  const { fmt } = useFormat();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('open'); // open, done, overdue, all

  const loadTodos = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const result = await mobileAPI.getTodos({ status: filter });
      if (result.ok !== false) {
        setTodos(result.items || []);
      }
    } catch (err) {
      console.error('Failed to load todos:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTodos();
  }, [filter]);

  const toggleTodo = async (todo) => {
    const newStatus = todo.status === 'done' ? 'open' : 'done';
    try {
      await mobileAPI.updateTodo(todo.id, { status: newStatus });
      // Optimistic update
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t))
      );
    } catch (err) {
      console.error('Failed to update todo:', err);
    }
  };

  const filterCounts = {
    open: todos.filter((t) => t.status === 'open').length,
    overdue: todos.filter((t) => t.status === 'overdue').length,
    done: todos.filter((t) => t.status === 'done').length,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-white">Taches</h1>
          <button
            onClick={() => loadTodos(true)}
            disabled={refreshing}
            className="p-2 text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { id: 'open', label: 'A faire', icon: Clock, color: 'blue' },
            { id: 'overdue', label: 'En retard', icon: AlertTriangle, color: 'red' },
            { id: 'done', label: 'Terminees', icon: Check, color: 'green' },
            { id: 'all', label: 'Toutes', icon: Filter, color: 'slate' },
          ].map((f) => {
            const Icon = f.icon;
            const isActive = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  isActive
                    ? f.color === 'blue'
                      ? 'bg-blue-500 text-white'
                      : f.color === 'red'
                      ? 'bg-red-500 text-white'
                      : f.color === 'green'
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-600 text-white'
                    : 'bg-slate-700 text-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {f.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Todo List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : todos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <CheckSquare className="w-12 h-12 mb-2 opacity-50" />
            <p>Aucune tache</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-700">
            {todos.map((todo) => (
              <li
                key={todo.id}
                className="px-4 py-3 flex items-start gap-3 active:bg-slate-800"
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleTodo(todo)}
                  className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    todo.status === 'done'
                      ? 'bg-green-500 border-green-500'
                      : 'border-slate-500 hover:border-blue-500'
                  }`}
                >
                  {todo.status === 'done' && <Check className="w-4 h-4 text-white" />}
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {/* Priority dot */}
                    <div
                      className={`w-2 h-2 rounded-full ${getPriorityColor(todo.priority)}`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        todo.status === 'done' ? 'text-slate-500 line-through' : 'text-white'
                      }`}
                    >
                      {todo.title}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {/* Vehicle */}
                    {todo.vehicle_license_plate && (
                      <span className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        {todo.vehicle_license_plate}
                      </span>
                    )}
                    {/* Due date */}
                    {todo.due_date && (
                      <span
                        className={`flex items-center gap-1 ${
                          todo.status === 'overdue' ? 'text-red-400' : ''
                        }`}
                      >
                        <Calendar className="w-3 h-3" />
                        {fmt.date(todo.due_date)}
                      </span>
                    )}
                  </div>

                  {/* Category tag */}
                  {todo.category && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-slate-700 text-slate-400 rounded-full">
                      {todo.category}
                    </span>
                  )}
                </div>

                {/* Status icon */}
                <div className="flex-shrink-0">{getStatusIcon(todo.status)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
